'use client'

import { ReactNode, useEffect, useRef, useState } from 'react'
import { Button, Carousel, Cell, Divider, Icon, Image, Spacer, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useAsset } from '@marketplace-web/shared/assets'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'
import { FaqEntryType } from '@marketplace-web/help-center/help-center-faq-entry-url-data'

import ConversationExample from '../ConversationExample'
import usePhishingFirstTimeListerEducationTracking from '../usePhishingFirstTimeListerEducationTracking'
import {
  AB_TEST_VARIANTS,
  MANDATORY_INTRO_SLIDE_TIME,
  type Conversation,
  type BulletPoint,
  type Slide,
} from './newContentTestConfig'

type SlidesProps = {
  setIsDialogOpen: (isOpen: boolean) => void
}

const Slides = ({ setIsDialogOpen }: SlidesProps) => {
  const educationContainerRef = useRef<HTMLDivElement>(null)
  const [isButtonEnabled, setIsButtonEnabled] = useState(false)
  const [isEducationStarted, setIsEducationStarted] = useState(false)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)

  const phishingEducationAbTest = useAbTest('first_time_lister_education_new_content')
  const isNewVariant = phishingEducationAbTest?.variant === 'on'

  useTrackAbTest(phishingEducationAbTest)

  const config = AB_TEST_VARIANTS[isNewVariant ? 'new' : 'old']

  const translate = useTranslate('conversation.phishing_first_time_lister_education')
  const asset = useAsset('/assets/phishing-first-time-lister-education')

  const { onSeeTipsClick, onSlideChange, onHelpCenterClick, onDialogClose } =
    usePhishingFirstTimeListerEducationTracking({
      educationContainerRef,
    })

  const lastSlideIndex = config.carouselSlides.length - 1
  const isLastSlide = lastSlideIndex === currentSlideIndex

  const helpCentreTranslationValues = {
    'link-help-centre': (chunks: Array<ReactNode>) => (
      <HelpCenterFaqEntryUrl
        key="link-help-centre"
        type={FaqEntryType.SpamPhishing}
        render={faqUrl => (
          <a href={faqUrl} target="_blank" rel="noreferrer" onClick={onHelpCenterClick}>
            {chunks}
          </a>
        )}
      />
    ),
  }

  useEffect(() => {
    const waitingTimer = setTimeout(() => {
      setIsButtonEnabled(true)
    }, MANDATORY_INTRO_SLIDE_TIME)

    return () => clearTimeout(waitingTimer)
  }, [])

  const getButtonTranslationKey = () => {
    if (!isEducationStarted) {
      return config.buttonTranslations.intro
    }

    if (isLastSlide) {
      return config.buttonTranslations.close
    }

    return 'actions.next'
  }

  const handleSlideChange = (index: number) => {
    onSlideChange(currentSlideIndex, index)
    setCurrentSlideIndex(index)
  }

  const handleButtonClick = () => {
    if (!isEducationStarted) {
      setIsEducationStarted(true)
      onSeeTipsClick()

      return
    }

    if (isLastSlide) {
      setIsDialogOpen(false)
      onDialogClose()

      return
    }

    handleSlideChange(currentSlideIndex + 1)
  }

  const formatMessages = (messages: Array<string>) =>
    messages.map(message => {
      if (message.includes('.png')) {
        return <Image src={asset(message)} size={Image.Size.X2Large} />
      }

      return <Text as="span" text={translate(message)} html />
    })

  const renderConversationItem = ({ messages, avatarImageSrc }: Conversation, index: number) => {
    const formattedMessages = formatMessages(messages)
    const label = config.showConversationLabels
      ? translate('scam_message_example', { index: index + 1 })
      : ''

    return (
      <ConversationExample
        key={index}
        label={label}
        messages={formattedMessages}
        avatarImageSrc={asset(avatarImageSrc)}
        showSpacer={config.showConversationLabels}
      />
    )
  }

  const renderSectionTitle = (sectionTitle?: string) => {
    if (!sectionTitle) return null

    return (
      <Text
        as="h3"
        text={translate(sectionTitle)}
        type={Text.Type.Title}
        alignment={Text.Alignment.Left}
        width={Text.Width.Parent}
      />
    )
  }

  const renderConversations = (conversations?: Array<Conversation>, sectionTitle?: string) => {
    if (!conversations?.length) return null

    const separatorSize = config.showConversationLabels ? Spacer.Size.X2Large : Spacer.Size.Large

    return (
      <>
        <Spacer size={Spacer.Size.X2Large} />
        {renderSectionTitle(sectionTitle)}
        {sectionTitle && <Spacer size={Spacer.Size.Regular} />}
        <SeparatedList separator={<Spacer size={separatorSize} />}>
          {conversations.map(renderConversationItem)}
        </SeparatedList>
      </>
    )
  }

  const renderBulletPoints = (bulletPoints?: Array<BulletPoint>) => {
    if (!bulletPoints?.length) return null

    return (
      <>
        <Spacer size={Spacer.Size.X2Large} />
        <div className="u-flexbox u-flex-direction-column u-align-items-start">
          {bulletPoints.map(({ text, icon }, index) => (
            <div
              key={index}
              className="u-flexbox u-align-items-center u-margin-bottom-medium u-padding-vertical-small"
            >
              <Cell
                prefix={<Icon name={icon} color={Icon.Color.Primary} />}
                title={translate(text)}
                styling={Cell.Styling.Tight}
              />
            </div>
          ))}
        </div>
      </>
    )
  }

  const renderReportSteps = (
    step1?: string,
    step2?: string,
    step3?: string,
    helpPrompt?: string,
    sectionTitle?: string,
  ) => {
    if (!step1 || !step2 || !step3) return null

    const steps = [step1, step2, step3]

    return (
      <>
        <Spacer size={Spacer.Size.X2Large} />
        {renderSectionTitle(sectionTitle)}
        <Spacer size={Spacer.Size.Regular} />
        <div className="phishing-first-time-lister-education__steps">
          <ol>
            {steps.map((text, index) => (
              <li key={index}>
                <Text
                  as="span"
                  text={translate(text)}
                  type={Text.Type.Subtitle}
                  theme="amplified"
                  inline
                />
              </li>
            ))}
          </ol>
        </div>
        {helpPrompt && (
          <>
            <Spacer size={Spacer.Size.Large} />
            <div className="u-force-wrap">{translate(helpPrompt, helpCentreTranslationValues)}</div>
          </>
        )}
      </>
    )
  }

  const renderSlide = (slide: Slide, index: number) => (
    <Cell key={index}>
      {config.showIntroSpacer && <Spacer size={Spacer.Size.X2Large} />}
      <div className="u-flexbox">
        <Image
          src={asset(slide.image, { theme: { dark: `dark/${slide.image}` } })}
          size={Image.Size.X3Large}
          alt={translate(slide.title)}
          scaling={Image.Scaling.Contain}
        />
      </div>
      <Spacer size={Spacer.Size.Large} />
      <Text
        as="h1"
        text={translate(slide.title)}
        type={config.titleType}
        alignment={Text.Alignment.Center}
        width={Text.Width.Parent}
      />
      <Spacer size={Spacer.Size.Large} />
      <Text
        as="span"
        text={
          <div className="u-force-wrap">{translate(slide.body, helpCentreTranslationValues)}</div>
        }
        type={Text.Type.Body}
        html
      />
      {renderBulletPoints(slide.bulletPoints)}
      {renderConversations(slide.conversations, slide.sectionTitle)}
      {renderReportSteps(
        slide.step1,
        slide.step2,
        slide.step3,
        slide.helpPrompt,
        slide.sectionTitle,
      )}
    </Cell>
  )

  const renderCarousel = () => {
    if (!isEducationStarted) {
      return renderSlide(config.introSlide, -1)
    }

    const currentSlide = config.carouselSlides[currentSlideIndex]

    if (currentSlide) {
      return renderSlide(currentSlide, currentSlideIndex)
    }

    return renderSlide(config.introSlide, -1)
  }

  const renderCarouselNavigation = () => {
    if (!isEducationStarted) return null

    return (
      <>
        <div className="u-flexbox">
          <Carousel.Navigation
            onBulletSelect={handleSlideChange}
            slidesCount={config.carouselSlides.length}
            activeSlide={currentSlideIndex}
            styling={Carousel.Styling.Floating}
          />
        </div>
        <Spacer size={Spacer.Size.Large} />
      </>
    )
  }

  const renderActions = () => (
    <>
      {isEducationStarted ? <Divider /> : null}
      <Cell>
        {renderCarouselNavigation()}
        <Button
          text={translate(getButtonTranslationKey())}
          styling={Button.Styling.Filled}
          onClick={handleButtonClick}
          disabled={!isButtonEnabled}
          testId="phishing-first-time-lister-education-action-button"
        />
      </Cell>
    </>
  )

  return (
    <div className="u-fill-height u-flexbox u-flex-direction-column">
      <div
        key={isEducationStarted ? currentSlideIndex : 'intro'}
        className="phishing-first-time-lister-education__slide"
        ref={educationContainerRef}
      >
        {renderCarousel()}
      </div>
      {renderActions()}
    </div>
  )
}

export default Slides
